import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import {
  FiUsers,
  FiArrowLeft,
  FiRefreshCw,
  FiSearch,
  FiAward,
  FiCheckCircle,
  FiUser,
  FiCalendar,
  FiChevronRight,
  FiZap,
  FiEye,
  FiRotateCcw,
  FiX,
  FiAlertTriangle,
  FiMinusCircle,
  FiShield,
  FiShieldOff
} from "react-icons/fi";
import toast from "react-hot-toast";
import {
  getEventParticipants,
  getEventById,
  getEvents,
  revertParticipantEventPoints,
  banParticipantFromEvent
} from "../api/eventApi";
import { formatUtcDateTime } from "../utils/dateUtils";
import {
  calculateUserCoins,
  setLocalPointDeduction,
} from "../utils/eventCoinUtils";

const REVERT_REASON_OPTIONS = [
  "Bot / Automated Activity",
  "Referral Fraud / Fake Accounts",
  "Duplicate Account Exploitation",
  "Event Terms & Rules Violation",
  "Admin Manual Adjustment",
  "Other / Custom Reason",
];

const EventParticipants = () => {
  const { id: routeEventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeEventId = routeEventId || searchParams.get("eventId") || "";

  // State
  const [eventsList, setEventsList] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const debounceTimer = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 400);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setPage(1);
    clearTimeout(debounceTimer.current);
  };

  // Revert Modal State
  const [revertModalOpen, setRevertModalOpen] = useState(false);
  const [selectedUserForRevert, setSelectedUserForRevert] = useState(null);
  const [revertCurrentScore, setRevertCurrentScore] = useState(0);
  const [revertAmount, setRevertAmount] = useState("");
  const [revertPreset, setRevertPreset] = useState("custom");
  const [revertReason, setRevertReason] = useState(REVERT_REASON_OPTIONS[0]);
  const [revertCustomNote, setRevertCustomNote] = useState("");
  const [revertLoading, setRevertLoading] = useState(false);

  const [banModalOpen, setBanModalOpen] = useState(false);
  const [selectedUserForBan, setSelectedUserForBan] = useState(null);
  const [banMessage, setBanMessage] = useState("");
  const [banLoading, setBanLoading] = useState(false);


  // 1. Fetch available events for the switcher dropdown
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const res = await getEvents({ page: 1, limit: 100 });
        const list = res?.data?.events || (Array.isArray(res?.data) ? res.data : []);
        setEventsList(list);

        if (!activeEventId && list.length > 0) {
          const targetId = list[0].eventId || list[0].id;
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("eventId", targetId);
            return next;
          });
        }
      } catch (err) {
        console.error("Failed to load events list", err);
      }
    };
    loadEvents();
  }, [activeEventId, setSearchParams]);

  // 2. Fetch specific event details
  useEffect(() => {
    if (!activeEventId) return;
    const loadEventDetails = async () => {
      try {
        const res = await getEventById(activeEventId);
        setSelectedEvent(res?.data || res);
      } catch (err) {
        console.warn("Failed to load event details", err);
      }
    };
    loadEventDetails();
  }, [activeEventId]);

  // 3. Fetch participants / leaderboard history
  const fetchParticipantsList = useCallback(async () => {
    if (!activeEventId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const isSearchActive = Boolean(debouncedSearch && debouncedSearch.trim());
      const isBannedFilter = filterStatus === "banned";
      const isLocalOverride = isSearchActive || isBannedFilter;
      const fetchLimit = isLocalOverride ? 5000 : limit;
      const fetchPage = isLocalOverride ? 1 : page;

      const res = await getEventParticipants(activeEventId, {
        page: fetchPage,
        limit: fetchLimit,
        search: debouncedSearch,
        isblock: isBannedFilter ? "true" : "",
      });

      const responseData = res?.data || res;
      let list =
        responseData?.participants ||
        responseData?.history ||
        (Array.isArray(responseData) ? responseData : []);
      let pagination = responseData?.pagination || {};

      if (isBannedFilter) {
        list = list.map(u => ({ ...u, isblock: true }));
      }


      // Local fallback for search if backend didn't filter
      if (isSearchActive) {
        const term = debouncedSearch.toLowerCase();
        list = list.filter((user) => {
          const uname = String(user.username || "").toLowerCase();
          const email = String(user.email || "").toLowerCase();
          const uid = String(user.userId || user.id || "");
          return uname.includes(term) || email.includes(term) || uid.includes(term);
        });
      }

      setParticipants(list);
      const total = isLocalOverride ? list.length : (pagination.total ?? list.length);
      setTotalRecords(total);
      const calculatedPages =
        Math.ceil(total / limit) || 1;
      setTotalPages(isLocalOverride ? Math.ceil(list.length / limit) || 1 : (pagination.totalPages ?? calculatedPages));
    } catch (err) {
      setError(err.message || "Failed to load event participants");
    } finally {
      setLoading(false);
    }
  }, [activeEventId, page, limit, debouncedSearch, filterStatus]);

  useEffect(() => {
    fetchParticipantsList();
  }, [fetchParticipantsList]);

  // Event Switcher handler
  const handleEventChange = (e) => {
    const newId = e.target.value;
    if (newId) {
      setPage(1);
      setSearchTerm("");
      setDebouncedSearch("");
      setFilterStatus("all");
      clearTimeout(debounceTimer.current);
      if (routeEventId) {
        navigate(`/events/${newId}/participants`);
      } else {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("eventId", newId);
          return next;
        });
      }
    }
  };

  // Filter list by search term
  const filteredParticipants = participants.filter((user) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const uname = String(user.username || "").toLowerCase();
    const email = String(user.email || "").toLowerCase();
    const uid = String(user.userId || user.id || "");
    return uname.includes(term) || email.includes(term) || uid.includes(term);
  });

  const isSearching = Boolean(debouncedSearch.trim() || searchTerm.trim());
  const paginatedParticipants = (isSearching && filterStatus !== "banned")
    ? filteredParticipants.slice((page - 1) * limit, page * limit)
    : filteredParticipants;

  // Calculate prize summary
  const prizesList = Array.isArray(selectedEvent?.prizes) ? selectedEvent.prizes : [];
  const totalZx = prizesList.reduce((acc, curr) => acc + (Number(curr.rewardZx) || 0), 0);
  const coinTotals = prizesList.reduce((acc, curr) => {
    const sym = curr.coinSymbol || (curr.rewardUsdt ? "USDT" : null);
    const amt = Number(curr.coinAmount ?? curr.rewardUsdt ?? 0);
    if (sym && amt > 0) acc[sym] = (acc[sym] || 0) + amt;
    return acc;
  }, {});

  // Revert Modal Handlers
  const handleOpenRevertModal = (user) => {
    const { earnedCoins, originalCoins, revertedPoints } = calculateUserCoins(
      user,
      {},
      selectedEvent,
      activeEventId
    );
    setSelectedUserForRevert({
      ...user,
      originalCoins,
      revertedPoints,
    });
    setRevertCurrentScore(earnedCoins);
    setRevertAmount(earnedCoins > 0 ? String(Math.min(1000, earnedCoins)) : "0");
    setRevertPreset("custom");
    setRevertReason(REVERT_REASON_OPTIONS[0]);
    setRevertCustomNote("");
    setRevertModalOpen(true);
  };

  const handleCloseRevertModal = () => {
    if (revertLoading) return;
    setRevertModalOpen(false);
    setSelectedUserForRevert(null);
  };

  const handlePresetSelect = (percentage) => {
    setRevertPreset(String(percentage));
    if (percentage === 100) {
      setRevertAmount(String(revertCurrentScore));
    } else {
      const calculated = Math.round((revertCurrentScore * percentage) / 100);
      setRevertAmount(String(calculated));
    }
  };

  const handleConfirmRevert = async (e) => {
    if (e) e.preventDefault();
    if (!selectedUserForRevert) return;

    const numPoints = Number(revertAmount);
    if (isNaN(numPoints) || numPoints <= 0) {
      toast.error("Please enter a valid positive number of points to revert.");
      return;
    }

    if (numPoints > revertCurrentScore) {
      toast.error(
        `Revert amount cannot exceed current score (${revertCurrentScore.toLocaleString()} coins).`
      );
      return;
    }

    const userId = selectedUserForRevert.userId || selectedUserForRevert.id;
    const historyId = selectedUserForRevert.historyId;
    const finalReason = `${revertReason}${
      revertCustomNote.trim() ? ` - ${revertCustomNote.trim()}` : ""
    }`;

    setRevertLoading(true);
    try {
      await revertParticipantEventPoints({
        eventId: activeEventId,
        userId,
        historyId,
        pointsToRevert: numPoints,
        reason: finalReason,
      });

      // Persist local deduction
      setLocalPointDeduction(activeEventId, userId, numPoints, {
        reason: finalReason,
        historyId,
      });

      // Update state locally
      setParticipants((prev) =>
        prev.map((item) => {
          const itemUid = String(item.userId || item.id || "");
          if (itemUid === String(userId)) {
            const existingReverted = Number(item.revertedPoints || 0);
            return {
              ...item,
              revertedPoints: existingReverted + numPoints,
            };
          }
          return item;
        })
      );

      toast.success(
        `Successfully reverted ${numPoints.toLocaleString()} points for ${
          selectedUserForRevert.username || `User #${userId}`
        }!`
      );
      setRevertModalOpen(false);
      setSelectedUserForRevert(null);
    } catch (err) {
      toast.error(err.message || "Failed to revert event points");
    } finally {
      setRevertLoading(false);
    }
  };

  const handleOpenBanModal = (user) => {
    setSelectedUserForBan(user);
    setBanMessage(user.message || "");
    setBanModalOpen(true);
  };
  
  const handleCloseBanModal = () => {
    setBanModalOpen(false);
    setSelectedUserForBan(null);
    setBanMessage("");
  };

  const handleBanSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!selectedUserForBan) return;
    setBanLoading(true);
    const userId = selectedUserForBan.userId || selectedUserForBan.id;
    const isblock = !selectedUserForBan.isblock;
    
    try {
      await banParticipantFromEvent(activeEventId, userId, isblock, banMessage);
      toast.success(isblock ? "User banned successfully from event!" : "User unbanned successfully!");
      
      setParticipants((prev) =>
        prev.map((item) => {
          const itemUid = String(item.userId || item.id || "");
          if (itemUid === String(userId)) {
            return {
              ...item,
              isblock,
              message: isblock ? banMessage : "",
            };
          }
          return item;
        })
      );
      
      handleCloseBanModal();
    } catch (err) {
      toast.error(err.message || "Failed to update ban status");
    } finally {
      setBanLoading(false);
    }
  };

  const renderRankBadge = (rank, isSearchMode = isSearching) => {
    const num = Number(rank);
    if (!isSearchMode) {
      if (num === 1) {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black bg-amber-100 text-amber-900 border border-amber-300 shadow-2xs">
            🥇 Rank 1
          </span>
        );
      }
      if (num === 2) {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black bg-slate-100 text-slate-800 border border-slate-300 shadow-2xs">
            🥈 Rank 2
          </span>
        );
      }
      if (num === 3) {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black bg-amber-50 text-amber-950 border border-amber-200 shadow-2xs">
            🥉 Rank 3
          </span>
        );
      }
    }
    if (num > 0) {
      return (
        <span
          className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 font-mono"
          title={isSearchMode ? `Search result #${num}` : `Rank #${num}`}
        >
          #{num}
        </span>
      );
    }
    return <span className="text-gray-400 text-xs font-medium">—</span>;
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-4 sm:p-8 space-y-5">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center flex-wrap gap-1.5 text-xs font-medium text-gray-500">
            <Link to="/events" className="hover:text-black transition flex items-center gap-1 hover:underline">
              <FiCalendar size={13} /> Events
            </Link>
            <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
            {activeEventId ? (
              <Link
                to={`/events/${activeEventId}`}
                className="hover:text-black transition max-w-xs truncate font-semibold hover:underline"
                title="View Event Details"
              >
                {selectedEvent?.eventName || activeEventId}
              </Link>
            ) : (
              <span className="text-gray-700 max-w-xs truncate font-semibold">
                {selectedEvent?.eventName || "Event"}
              </span>
            )}
            <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
            <span className="text-black font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
              Participants List
            </span>
          </div>

          {/* Top Bar Header & Controls */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else if (activeEventId) {
                    navigate(`/events/${activeEventId}`);
                  } else {
                    navigate("/events");
                  }
                }}
                className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
                title="Back to Previous Page"
                aria-label="Back"
              >
                <FiArrowLeft size={18} />
              </button>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                    Event Participants
                  </h1>
                  {selectedEvent?.status && (
                    <span
                      className={`text-xs px-2.5 py-0.5 rounded-full font-bold uppercase ${
                        selectedEvent.status === "completed"
                          ? "bg-blue-50 text-blue-700 border border-blue-200"
                          : selectedEvent.status === "running"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : selectedEvent.status === "upcoming"
                          ? "bg-amber-50 text-amber-700 border border-amber-200"
                          : "bg-gray-100 text-gray-600 border border-gray-200"
                      }`}
                    >
                      {selectedEvent.status}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  Review enrolled users, check-in history, points, and leaderboard for {selectedEvent?.eventName || activeEventId}
                </p>
              </div>
            </div>

            {/* Actions & Event Switcher */}
            <div className="flex flex-wrap items-center gap-2.5">
              {/* Event Switcher */}
              <div className="flex items-center gap-1.5 bg-white px-3 py-2 border border-gray-200 rounded-xl shadow-xs text-xs">
                <FiAward className="text-amber-500 flex-shrink-0" size={15} />
                <span className="text-gray-400 font-medium">Event:</span>
                <select
                  value={activeEventId}
                  onChange={handleEventChange}
                  className="bg-transparent font-bold text-gray-800 focus:outline-none cursor-pointer max-w-[180px] sm:max-w-[220px] truncate"
                >
                  {eventsList.map((evt) => (
                    <option key={evt.eventId || evt.id} value={evt.eventId || evt.id}>
                      {evt.eventName} ({evt.status})
                    </option>
                  ))}
                </select>
              </div>

              {/* Refresh Button */}
              <button
                onClick={fetchParticipantsList}
                disabled={loading}
                className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-600 transition shadow-xs cursor-pointer"
                title="Refresh List"
              >
                <FiRefreshCw size={15} className={loading ? "animate-spin" : ""} />
              </button>

              {/* Shortcut to Winner Approvals */}
              <button
                onClick={() => navigate(`/events/${activeEventId}/winners`)}
                className="flex items-center gap-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 text-xs font-bold px-3.5 py-2.5 rounded-xl shadow-xs transition cursor-pointer"
              >
                <FiAward size={14} />
                <span>Winner Approvals</span>
              </button>
            </div>
          </div>

          {/* Metric Stat Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-3.5">
            {/* Card 1: Total Participants */}
            <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs flex items-center justify-between min-w-0">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-[11px] font-bold text-amber-700 uppercase tracking-wider truncate">
                  Participants
                </p>
                <p className="text-lg font-black text-gray-900 mt-0.5 truncate">
                  {Number(selectedEvent?.participantCount ?? totalRecords).toLocaleString()}
                </p>
                <p className="text-[10px] text-gray-400 truncate">Enrolled Users</p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center font-bold flex-shrink-0">
                <FiUsers size={16} />
              </div>
            </div>

            {/* Card 2: Total Check-ins */}
            <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs flex items-center justify-between min-w-0">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider truncate">
                  Check-ins
                </p>
                <p className="text-lg font-black text-gray-900 mt-0.5 truncate">
                  {selectedEvent?.totalCheckins != null
                    ? Number(selectedEvent.totalCheckins).toLocaleString()
                    : "—"}
                </p>
                <p className="text-[10px] text-gray-400 truncate">Daily Tasks</p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold flex-shrink-0">
                <FiCheckCircle size={16} />
              </div>
            </div>

            {/* Card 3: Check-in Coins Rate */}
            <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs flex items-center justify-between min-w-0">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-[11px] font-bold text-[#999900] uppercase tracking-wider truncate">
                  Check-in Rate
                </p>
                <p className="text-lg font-black text-gray-900 mt-0.5 truncate">
                  🪙 {(selectedEvent?.checkInCoin ?? selectedEvent?.checkinCoin ?? selectedEvent?.checkInPoints ?? selectedEvent?.coin ?? 0).toLocaleString()}
                </p>
                <p className="text-[10px] text-gray-400 truncate">Coins / Check-in</p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-yellow-50 text-yellow-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                🪙
              </div>
            </div>

            {/* Card 4: Referral Rate */}
            <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs flex items-center justify-between min-w-0">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-[11px] font-bold text-blue-700 uppercase tracking-wider truncate">
                  Referral Rate
                </p>
                <p className="text-lg font-black text-gray-900 mt-0.5 truncate">
                  👥 {(selectedEvent?.referralCoin ?? selectedEvent?.referralCoins ?? selectedEvent?.referralPoints ?? selectedEvent?.referralBonus ?? 50).toLocaleString()}
                </p>
                <p className="text-[10px] text-gray-400 truncate">Coins / Refer</p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                👥
              </div>
            </div>

            {/* Card 5: Mining Cycle Rate */}
            <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs flex items-center justify-between min-w-0">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-[11px] font-bold text-teal-700 uppercase tracking-wider truncate">
                  Mining Rate
                </p>
                <p className="text-lg font-black text-gray-900 mt-0.5 truncate">
                  ⚡ {(selectedEvent?.miningCycleCoin ?? selectedEvent?.miningCycleCoins ?? selectedEvent?.miningCycle ?? selectedEvent?.miningCyclePoints ?? selectedEvent?.miningCoin ?? 0).toLocaleString()}
                </p>
                <p className="text-[10px] text-gray-400 truncate">Coins / Cycle</p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                ⚡
              </div>
            </div>

            {/* Card 6: Browser Rate */}
            <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs flex items-center justify-between min-w-0">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider truncate">
                  Browser Rate
                </p>
                <p className="text-lg font-black text-gray-900 mt-0.5 truncate">
                  🌐 {(selectedEvent?.browserPointsPerZx ?? selectedEvent?.browserPointsRate ?? 10).toLocaleString()}
                </p>
                <p className="text-[10px] text-gray-400 truncate">Pts / ZX Mined</p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                🌐
              </div>
            </div>

            {/* Card 7: Prize Pool */}
            <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs flex items-center justify-between min-w-0">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-[11px] font-bold text-purple-700 uppercase tracking-wider truncate">
                  Prize Pool
                </p>
                <p className="text-sm font-black text-purple-700 mt-0.5 truncate">
                  {totalZx > 0 ? `${totalZx.toLocaleString()} ZX` : "Event Prizes"}
                </p>
                <p className="text-[10px] text-emerald-700 font-bold truncate">
                  {Object.entries(coinTotals).length > 0
                    ? Object.entries(coinTotals).map(([s, a]) => `${a} ${s}`).join(" + ")
                    : totalZx > 0 ? "ZX Tokens" : "Leaderboard"}
                </p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center font-bold flex-shrink-0">
                <FiAward size={16} />
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <FiSearch
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                size={15}
              />
              <input
                type="text"
                placeholder="Search by username, email, user ID..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded cursor-pointer"
                  title="Clear search"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ffff00] cursor-pointer"
              >
                <option value="all">All Users</option>
                <option value="banned">Banned Users</option>
              </select>

              <div className="flex flex-col items-end gap-1">
                {isSearching && (
                  <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg font-semibold flex items-center gap-1">
                    🔍 Filtered: "{debouncedSearch || searchTerm}"
                  </span>
                )}
                <span className="text-xs text-gray-400 font-medium">
                  {isSearching
                    ? `Found ${totalRecords} Matching User(s)`
                    : `Total ${totalRecords} Participant User(s)`}
                </span>
              </div>
            </div>
          </div>

          {/* Ranking Rule Note */}
          <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl px-4 py-2.5 flex items-center justify-between text-xs text-amber-900 shadow-xs">
            <div className="flex items-center gap-2">
              <span className="text-base">📊</span>
              <span>
                <strong>Leaderboard Ranking Basis:</strong> Users are ranked based on <strong>Total Coins Earned from Daily Check-ins (🪙 Check-in Coins)</strong> (Tie-breaker: <strong>Earliest Completion Date/Time</strong>).
                {isSearching && " In search mode, results show matching users; global rank badges and rewards apply to the overall event leaderboard."}
              </span>
            </div>
          </div>

          {/* Participants Table */}
          {loading && participants.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-16 flex flex-col items-center justify-center gap-3">
              <div className="w-8 h-8 border-3 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-gray-400">Loading participant users...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-sm flex flex-col items-center gap-3 text-center">
              <p className="font-semibold">{error}</p>
              <button
                onClick={fetchParticipantsList}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition"
              >
                Retry
              </button>
            </div>
          ) : paginatedParticipants.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-16 text-center space-y-3 shadow-xs">
              <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center mx-auto text-xl font-bold">
                👥
              </div>
              <h3 className="text-sm font-bold text-gray-800">No Participants Found</h3>
              <p className="text-xs text-gray-400 max-w-sm mx-auto">
                {searchTerm
                  ? "No participants match your search query."
                  : "No users have enrolled in or checked into this event yet."}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200/80 shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-[11px] text-gray-500 uppercase font-bold tracking-wider">
                      <th className="px-5 py-3 w-20 text-center">
                        {isSearching ? "Result #" : "Rank"}
                      </th>
                      <th className="px-5 py-3 min-w-[220px]">User Info</th>
                      <th className="px-5 py-3 text-center min-w-[170px]">
                        <span title="Total Coins earned by user via Daily Check-ins">
                          Check-in Coins (Score)
                        </span>
                      </th>
                      <th className="px-5 py-3 min-w-[200px]">Rewards Won</th>
                      <th className="px-5 py-3 whitespace-nowrap">Join / Task Date</th>
                      <th className="px-5 py-3 text-center">Status</th>
                      <th className="px-5 py-3 text-center whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedParticipants.map((user, idx) => {
                      const rank = user.userRank ?? user.rank ?? (page - 1) * limit + idx + 1;
                      const displayRankNumber = isSearching
                        ? (user.globalRank || (page - 1) * limit + idx + 1)
                        : rank;
                      const {
                        earnedCoins,
                        originalCoins,
                        revertedPoints,
                        checkinCount: checkins,
                      } = calculateUserCoins(
                        user,
                        {},
                        selectedEvent,
                        activeEventId
                      );
                      const zxReward = Number(user.rewardZx) || 0;
                      const coinReward = user.rewardCoin;
                      const hasCoin =
                        coinReward &&
                        (Number(coinReward.coin) > 0 || Number(coinReward.coinAmount) > 0);
                      const coinSym = coinReward?.coinSymbol || coinReward?.symbol || "USDT";
                      const coinAmt = coinReward?.coin ?? coinReward?.coinAmount ?? 0;

                      const hasCreditedReward = user.rewardStatus === "credited";
                      const isConfirmedWinner =
                        user.isWinner === true ||
                        (!isSearching && prizesList.length > 0 && rank <= prizesList.length);
                      const showRewards = hasCreditedReward || isConfirmedWinner;

                      return (
                        <tr
                          key={user.historyId || user.userId || user.id || idx}
                          className="hover:bg-yellow-50/20 transition"
                        >
                          {/* Rank / Result # */}
                          <td className="px-5 py-3.5 text-center whitespace-nowrap">
                            {renderRankBadge(displayRankNumber, isSearching)}
                          </td>

                          {/* User Info - Click directly opens User Profile Page */}
                          <td className="px-5 py-3.5">
                            <Link
                              to={`/user/${user.userId || user.id}`}
                              className="flex items-center gap-2.5 group cursor-pointer"
                              title={`Open User Details for ${user.username || "User"} (#${user.userId || user.id})`}
                            >
                              <div className="w-8 h-8 rounded-full bg-amber-100 group-hover:bg-[#ffff00] text-black flex items-center justify-center font-bold text-xs flex-shrink-0 transition shadow-2xs">
                                {user.username ? user.username.charAt(0).toUpperCase() : <FiUser />}
                              </div>
                              <div className="min-w-0">
                                <span className="font-bold text-gray-900 group-hover:text-blue-600 block truncate text-xs transition">
                                  {user.username || "Anonymous Miner"}
                                </span>
                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-400">
                                  <span className="truncate max-w-[160px]">{user.email || "No email"}</span>
                                  <span className="font-mono text-gray-500 bg-gray-100 group-hover:bg-blue-50 group-hover:text-blue-600 px-1 rounded text-[10px] transition">
                                    #{user.userId || user.id}
                                  </span>
                                </div>
                              </div>
                            </Link>
                          </td>

                          {/* Check-in Coins Earned */}
                          <td className="px-5 py-3.5 text-center whitespace-nowrap">
                            <div className="inline-flex flex-col items-center gap-0.5">
                              <span className="inline-flex items-center gap-1 font-black text-xs bg-amber-50 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-lg shadow-xs">
                                <span>🪙</span> {originalCoins.toLocaleString()} Coins
                              </span>
                              {revertedPoints > 0 ? (
                                <span
                                  className="text-[10px] text-rose-700 font-bold bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded flex items-center gap-1"
                                  title={`Original Score: ${originalCoins.toLocaleString()} Coins | Reverted/Deducted: ${revertedPoints.toLocaleString()} Coins | Current Net: ${earnedCoins.toLocaleString()} Coins`}
                                >
                                  <FiMinusCircle size={10} className="text-rose-600 flex-shrink-0" />
                                  <span>-{revertedPoints.toLocaleString()} reverted (Net: {earnedCoins.toLocaleString()})</span>
                                </span>
                              ) : null}
                              {checkins > 0 && (
                                <span className="text-[10px] text-gray-400 font-medium">
                                  ({checkins} check-in{checkins > 1 ? "s" : ""})
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Rewards */}
                          <td className="px-5 py-3.5">
                            {showRewards && (zxReward > 0 || (hasCoin && Number(coinAmt) > 0)) ? (
                              <div className="flex flex-wrap items-center gap-1.5">
                                {zxReward > 0 && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">
                                    <FiZap size={12} className="text-purple-600 fill-purple-600" />
                                    {zxReward.toLocaleString()} ZX
                                  </span>
                                )}
                                {hasCoin && Number(coinAmt) > 0 && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                                    <span>🪙</span> {coinAmt} {coinSym}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 font-medium">—</span>
                            )}
                          </td>

                          {/* Join / Date */}
                          <td className="px-5 py-3.5 whitespace-nowrap text-xs text-gray-600">
                            {user.createdAt || user.joinedAt || user.completionDate ? (
                              formatUtcDateTime(user.createdAt || user.joinedAt || user.completionDate)
                            ) : (
                              "—"
                            )}
                          </td>

                          {/* Status */}
                          <td className="px-5 py-3.5 text-center whitespace-nowrap">
                            {hasCreditedReward ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                🟢 Credited
                              </span>
                            ) : showRewards && user.rewardStatus === "pending" ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                🟡 Pending
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">
                                Enrolled
                              </span>
                            )}
                          </td>

                          {/* Action */}
                          <td className="px-5 py-3.5 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1.5">
                              <Link
                                to={`/user/${user.userId || user.id}/event-audit${activeEventId ? `?eventId=${encodeURIComponent(activeEventId)}` : ""}`}
                                state={{
                                  originalCoins,
                                  eventId: activeEventId,
                                }}
                                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-[#ffff00] text-black border border-gray-200 transition shadow-xs"
                                title="Open User Event Details"
                              >
                                <FiEye size={13} /> User Details
                              </Link>
                              <button
                                type="button"
                                onClick={() => handleOpenRevertModal(user)}
                                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 transition shadow-xs cursor-pointer"
                                title={`Revert/Deduct custom points for ${user.username || "User"}`}
                              >
                                <FiRotateCcw size={12} className="text-rose-600" />
                                <span>Revert Points</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleOpenBanModal(user)}
                                className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition shadow-xs cursor-pointer ${user.isblock ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'}`}
                                title={user.isblock ? "Unban user from event" : "Ban user from event"}
                              >
                                {user.isblock ? <FiShield size={12} className="text-emerald-600" /> : <FiShieldOff size={12} className="text-red-600" />}
                                <span>{user.isblock ? "Unban" : "Ban User"}</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination Controls */}
          {filterStatus !== "banned" && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>Show</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-1 border border-gray-200 rounded-md bg-white text-xs"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>per page (Total {totalRecords})</span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-2.5 py-1 text-xs font-semibold border rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  &laquo; Prev
                </button>
                <span className="px-3 py-1 text-xs font-bold bg-[#ffff00] text-black rounded-md">
                  {page} / {totalPages}
                </span>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-2.5 py-1 text-xs font-semibold border rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next &raquo;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Revert / Deduct Points Modal */}
      {revertModalOpen && selectedUserForRevert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-150 my-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-rose-50 via-red-50 to-amber-50 border-b border-rose-100 p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center font-bold shadow-xs">
                  <FiRotateCcw size={20} />
                </div>
                <div>
                  <h3 className="text-base font-black text-gray-900">
                    Revert Event Points
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Deduct or revert custom points for this participant
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseRevertModal}
                disabled={revertLoading}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-white/80 rounded-lg transition cursor-pointer"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Participant Profile & Score Summary */}
              <div className="bg-gray-50 border border-gray-200/80 rounded-xl p-3.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-amber-100 text-black flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-2xs">
                    {selectedUserForRevert.username
                      ? selectedUserForRevert.username.charAt(0).toUpperCase()
                      : <FiUser />}
                  </div>
                  <div className="min-w-0">
                    <span className="font-bold text-gray-900 block truncate text-xs">
                      {selectedUserForRevert.username || "Anonymous Miner"}
                    </span>
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                      <span className="truncate max-w-[140px]">{selectedUserForRevert.email || "No email"}</span>
                      <span className="font-mono text-gray-600 bg-white px-1 py-0.5 rounded border border-gray-200 text-[10px]">
                        #{selectedUserForRevert.userId || selectedUserForRevert.id}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  <span className="text-[10px] uppercase font-bold text-gray-400 block">
                    Current Active Score
                  </span>
                  <span className="inline-flex items-center gap-1 font-black text-xs bg-amber-100 text-amber-900 px-2 py-0.5 rounded-md mt-0.5">
                    🪙 {revertCurrentScore.toLocaleString()} Coins
                  </span>
                  {selectedUserForRevert?.revertedPoints > 0 && (
                    <span className="text-[10px] text-rose-600 block mt-0.5 font-semibold">
                      (Orig: {selectedUserForRevert.originalCoins?.toLocaleString() || revertCurrentScore} | -{selectedUserForRevert.revertedPoints?.toLocaleString()} prev)
                    </span>
                  )}
                </div>
              </div>

              {/* Point Amount Input & Presets */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                    <span>Points to Revert / Deduct</span>
                    <span className="text-rose-500">*</span>
                  </label>
                  <span className="text-[11px] text-gray-400">
                    Max: {revertCurrentScore.toLocaleString()} pts
                  </span>
                </div>

                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
                    🪙
                  </span>
                  <input
                    type="number"
                    min="1"
                    max={revertCurrentScore}
                    step="1"
                    placeholder="e.g. 5000"
                    value={revertAmount}
                    onChange={(e) => {
                      setRevertAmount(e.target.value);
                      setRevertPreset("custom");
                    }}
                    className="w-full pl-9 pr-20 py-2.5 border border-gray-200 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:border-black"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">
                    Points
                  </span>
                </div>

                {/* Quick Presets */}
                <div className="flex items-center gap-1.5 pt-1">
                  <span className="text-[11px] font-semibold text-gray-500 mr-1">Quick:</span>
                  {[
                    { label: "25%", val: 25 },
                    { label: "50%", val: 50 },
                    { label: "75%", val: 75 },
                    { label: "100% (All)", val: 100 },
                  ].map((p) => (
                    <button
                      key={p.val}
                      type="button"
                      onClick={() => handlePresetSelect(p.val)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold transition border cursor-pointer ${
                        revertPreset === String(p.val)
                          ? "bg-rose-100 text-rose-800 border-rose-300 shadow-2xs"
                          : "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Real-time Calculation Breakdown Preview */}
              <div className="bg-rose-50/50 border border-rose-100 rounded-xl p-3 space-y-1.5 text-xs">
                <div className="flex justify-between items-center text-gray-600">
                  <span>Current Leaderboard Points:</span>
                  <span className="font-bold text-gray-800">
                    🪙 {revertCurrentScore.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center text-rose-600 font-semibold">
                  <span>Revert / Deduction:</span>
                  <span>- 🪙 {(Number(revertAmount) || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-rose-200/60 pt-1.5 flex justify-between items-center font-black text-gray-900">
                  <span>New Balance After Revert:</span>
                  <span className="text-emerald-700 font-extrabold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    🪙 {Math.max(0, revertCurrentScore - (Number(revertAmount) || 0)).toLocaleString()} Coins
                  </span>
                </div>
              </div>

              {/* Reason Selector */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700 block">
                  Revert Reason
                </label>
                <select
                  value={revertReason}
                  onChange={(e) => setRevertReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-white cursor-pointer"
                >
                  {REVERT_REASON_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  placeholder="Optional custom admin note (e.g. Ticket #4829 or specific check-in date)..."
                  value={revertCustomNote}
                  onChange={(e) => setRevertCustomNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                />
              </div>

              {/* Warning Notice */}
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                <FiAlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={15} />
                <span>
                  <strong>Notice:</strong> This action will deduct <strong>{(Number(revertAmount) || 0).toLocaleString()} points</strong> from this user's score for <strong>{selectedEvent?.eventName || activeEventId}</strong>.
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={handleCloseRevertModal}
                disabled={revertLoading}
                className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-bold text-gray-700 hover:bg-gray-100 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRevert}
                disabled={
                  revertLoading ||
                  !Number(revertAmount) ||
                  Number(revertAmount) <= 0 ||
                  Number(revertAmount) > revertCurrentScore
                }
                className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-xs font-bold shadow-xs transition cursor-pointer"
              >
                {revertLoading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Reverting Points...</span>
                  </>
                ) : (
                  <>
                    <FiRotateCcw size={13} />
                    <span>Confirm Revert ({(Number(revertAmount) || 0).toLocaleString()} Pts)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ban / Unban Modal */}
      {banModalOpen && selectedUserForBan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150 my-auto">
            <div className={`p-5 flex items-center justify-between border-b ${!selectedUserForBan.isblock ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold shadow-xs ${!selectedUserForBan.isblock ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {!selectedUserForBan.isblock ? <FiShieldOff size={20} /> : <FiShield size={20} />}
                </div>
                <div>
                  <h3 className="text-base font-black text-gray-900">
                    {!selectedUserForBan.isblock ? "Ban Participant" : "Unban Participant"}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {selectedUserForBan.username || `User #${selectedUserForBan.userId || selectedUserForBan.id}`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseBanModal}
                disabled={banLoading}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-white/80 rounded-lg transition cursor-pointer"
              >
                <FiX size={18} />
              </button>
            </div>
            
            <form onSubmit={handleBanSubmit} className="p-6 space-y-4">
              {!selectedUserForBan.isblock && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    Ban Reason / Message to User (Optional)
                  </label>
                  <textarea
                    value={banMessage}
                    onChange={(e) => setBanMessage(e.target.value)}
                    placeholder="E.g. You have been banned for botting."
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#ffff00] resize-none"
                    rows="3"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">This message will be shown to the user on their leaderboard.</p>
                </div>
              )}
              {selectedUserForBan.isblock && (
                <p className="text-sm text-gray-600">
                  Are you sure you want to <b>unban</b> this user? They will immediately reappear on the leaderboard if they qualify.
                </p>
              )}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 mt-4">
                <button
                  type="button"
                  onClick={handleCloseBanModal}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-bold text-gray-700 hover:bg-gray-100 transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={banLoading}
                  className={`flex items-center gap-1.5 px-4 py-2 text-white rounded-xl text-xs font-bold shadow-xs transition cursor-pointer disabled:opacity-50 ${!selectedUserForBan.isblock ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                >
                  {banLoading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{!selectedUserForBan.isblock ? "Banning..." : "Unbanning..."}</span>
                    </>
                  ) : (
                    <span>{!selectedUserForBan.isblock ? "Confirm Ban" : "Confirm Unban"}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventParticipants;
